import fs from 'fs-extra';
import path from 'path';
const remote = require('@electron/remote');
import JSZip from 'jszip';

import store from '@/store';
import { AssetTypes } from '@/services/constants';
import { updateArtwork } from './ArtworkServices';

/**
 * @typedef FileModel
 * @property {String} id
 * @property {String} type
 * @property {String} path
 */
/**
 * @typedef ArtworkModel
 * @property {String} id
 * @property {FileModel[]} files
 */

const formattedTitles = {};
formattedTitles[AssetTypes.VIDEO_OPAQUE] = 'Video';
formattedTitles[AssetTypes.VIDEO_ALPHA] = 'Transparent Video';
formattedTitles[AssetTypes.IMAGE] = 'Image';
formattedTitles[AssetTypes.GLB] = '3D Model';
formattedTitles['qrCode'] = 'QR Code';

/**
 * Generates a readable panels asset type
 *
 * @param {String} assetType - Value in AssetTypes array
 * @returns
 */
export const getReadablePanelsAssetType = (assetType) => {
  return formattedTitles[assetType];
};

/**
 *
 * @param {ArtworkModel} artwork Artwork object
 * @returns {FileModel[]} Array of File objects that are valid panels assets
 */
export const getAssetFilesFromArtwork = (artwork) => {
  if (artwork && artwork.files) {
    // Filter out invalid asset types i.e. qrCode
    return artwork.files.filter((file) => {
      return Object.values(AssetTypes).includes(file.type);
    });
  }
  return [];
};

/**
 * Updates an artwork scene graph
 *
 * @param {String} artworkId
 * @param {String} sceneConfig
 * @returns
 */
export function updateArtworkSceneConfig(artworkId, sceneConfig) {
  console.log('Panels Editor: Saving artwork scene graph.');
  return updateArtwork(artworkId, { sceneConfig });
}

/**
 * Takes an array of fileIDs to delete, removes them from both artwork.files and from the scene graph, saves results in vuex and database
 * @param {String} artworkId
 * @param {String[]} fileIdsToDelete
 * @returns {Promise<ArtworkModel|Error>}
 */
export async function deleteFilesAndPanelsFromArtwork(artworkId, fileIdsToDelete) {
  console.log(`PanelsServices: Deleting files ${fileIdsToDelete} and panels from ${artworkId}.`);
  const artwork = store.getters.artworkById(artworkId);

  // Remove files from artwork object
  const files = artwork.files.filter(
    (file) => !fileIdsToDelete.some((fileID) => file.fileID === fileID)
  );
  let { sceneConfig } = artwork;

  // remove associated panels
  if (artwork.sceneConfig) {
    console.log('PanelsServices: \tRemoving associated panels from saved sceneconfig.');
    const sceneConfigObject = JSON.parse(sceneConfig);
    // Map through scenes
    sceneConfigObject.scenes = sceneConfigObject.scenes.map((scene) => {
      // Filter out children that reference assets being deleted
      scene.children = scene.children.filter(
        (child) => !fileIdsToDelete.includes(child.assetID)
      );
      return scene;
    });
    sceneConfig = JSON.stringify(sceneConfigObject);
  }
  // Commit changes to vuex / database
  return updateArtwork(artworkId, { sceneConfig, files });
}

const exportPanelsBundleZipStatuses = {
  DOWNLOADING_ASSETS: 'Downloading Assets...',
  EXPORTING_SCENE: 'Exporting Scene...',
  OPENING_DIALOG: 'Select save location.',
  BUNDLING_ZIP: 'Saving bundle to location...',
  COMPLETE: 'Saving complete.',
};
/**
 * @description Saves an artwork as a bundled zip to a location on the disk
 *
 * @export
 * @param {String} artworkId
 * @param {AWS.Amplify.Essential} essentialCredentials
 * @param {Function} [onStatusChange=({status}) => {}]
 * @returns 
 */
export function exportPanelsBundleZip(artworkId, onStatusChange = () => {}) {
  return new Promise(async (resolve, reject) => {
    try {
      console.debug(`Panels Services: Exporting ${artworkId} as zip.`);
      const artwork = store.getters.artworkById(artworkId);
      if (!artwork.artworkType === 'worldTarget' || !artwork.sceneConfig) {
        reject(new Error('Panels Service: Error exporting panels bundled zip.  Artwork provided is not a panels artwork.'));
      }

      if(Array.isArray(!artwork.files)) {
        reject(new Error('Panels Service: Error exporting panels bundled zip.  Artwork has no files/assets to load.'));
      } 
      console.debug('\t Passed input validation.');
      
      // Setup working zip
      const zip = new JSZip();
      const panelsFolder = zip.folder('panels');

      // Download assets from S3, add to working zip
      console.debug('\t Downloading assets...');
      onStatusChange({ status: exportPanelsBundleZipStatuses.DOWNLOADING_ASSETS });

      const downloadPromises = artwork.files.map( file => {
        return new Promise((resolve, reject) => {
          // Fetch the file
          const name = path.basename(file.path);
          fetch(file.path).then(response => {
            if (response.status === 200) {
              // Convert to blob
              response.blob().then(blob => {
                // Add to zip
                console.debug(`\t\t Adding ${name} to zip (size: ${blob.size})`);
                panelsFolder.file(name, blob);
                resolve();
              });
            } else {
              console.error(`\t\t Error retreiving asset ${name}.  Status code ${response.status}`);
              reject();
            }
          });
        });
      });
      await Promise.all(downloadPromises);
      console.debug(`\t Downloading assets DONE! Downloaded ${downloadPromises.length} assets.`);

      // Save sceneConfig json to working zip
      console.debug('\t Exporting scene config json...');
      onStatusChange({ status: exportPanelsBundleZipStatuses.EXPORTING_SCENE });
      panelsFolder.file('config.json', artwork.sceneConfig);
      console.debug('\t Exporting scene config json... DONE!');

      // Choose save location for bundle
      onStatusChange({ status: exportPanelsBundleZipStatuses.OPENING_DIALOG });
      const { canceled, filePath } = await remote.dialog.showSaveDialog({
        title: 'Select save location for bundle',
        buttonLabel: 'Save Bundle',
        defaultPath: '~/Desktop/' + artwork.name + '.zip',
        filters: [
          {
            name: 'Bundled Zip',
            extensions: ['zip'],
          },
        ],
      });
      if (canceled) return;
      const outputPath = filePath;

      // Generate ZIP as nodebuffer that can be written to disk.
      console.debug('\t Bundling as zip...');
      onStatusChange({ status: exportPanelsBundleZipStatuses.BUNDLING_ZIP });
      const bundleArrayBuffer = await zip.generateAsync({
        type: 'nodebuffer',
      });
      console.debug('\t Bundling as zip... DONE!');

      // Write ZIP to disk from given save location
      console.debug('\t Writing to disk...');
      fs.writeFileSync(outputPath, bundleArrayBuffer);
      console.debug('\t Writing to disk... DONE!');

      // Process complete, resolve
      onStatusChange({ status: exportPanelsBundleZipStatuses.COMPLETE });
      resolve(outputPath);

    } catch(error) {
      reject(error);
    }
  });
}

